import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import ConfirmDialog from "components/generic/confirm-dialog";
import { Metaform, MetaformVersion, MetaformVersionType, User } from "generated/client";
import strings from "localization/strings";
import moment from "moment";
import React, { FC, useState } from "react";

interface Props {
  loading: boolean;
  metaforms: Metaform[];
  metaformVersions: MetaformVersion[];
  lastModifiers: User[];
  deleteMetaformOrVersion: (id: string) => void;
  goToEditor: (id: string) => void;
}

/**
 * Gets display colors for a form version type.
 */
const getVersionColors = (type?: MetaformVersionType) => {
  if (type === MetaformVersionType.Draft) return { backgroundColor: "#fdf0e0", color: "#a55b06" };
  if (type === MetaformVersionType.Archived) return { backgroundColor: "#f0f2f1", color: "#6e7472" };
  return { backgroundColor: "#e4efe9", color: "#1c5f49" };
};

/**
 * Displays available forms as compact selection cards for the editor.
 */
const EditorFormSelector: FC<Props> = ({
  loading,
  metaforms,
  metaformVersions,
  lastModifiers,
  deleteMetaformOrVersion,
  goToEditor
}) => {
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState(false);
  const [ selectedId, setSelectedId ] = useState<string>();

  /**
   * Closes the form deletion confirmation dialog.
   */
  const closeDeleteDialog = () => {
    setSelectedId(undefined);
    setDeleteDialogOpen(false);
  };

  /**
   * Deletes the selected form after confirmation.
   */
  const confirmDelete = () => {
    if (selectedId) deleteMetaformOrVersion(selectedId);
    closeDeleteDialog();
  };

  /**
   * Gets editable form versions ordered by recent update.
   */
  const getVersions = (metaform: Metaform) => metaformVersions
    .filter(version => (version.data as Metaform).id === metaform.id)
    .sort((first, second) => (second.modifiedAt?.getTime() || 0) - (first.modifiedAt?.getTime() || 0));

  /**
   * Gets the localized display label for a form version.
   */
  const getVersionLabel = (version: MetaformVersion) => {
    if (version.type === MetaformVersionType.Draft) return strings.editorScreen.formVersionDraft;
    if (version.type === MetaformVersionType.Archived) return strings.editorScreen.formVersionArchived;
    return strings.editorScreen.formProductionVersion;
  };

  if (!loading && !metaforms.length) {
    return <Typography color="text.secondary">{ strings.editorScreen.noMetaforms }</Typography>;
  }

  return (
    <>
      <Stack spacing={ 1.5 } sx={{ maxWidth: 980, width: "100%" }}>
        { metaforms.map(metaform => {
          const versions = getVersions(metaform);
          const lastModifier = lastModifiers.find(user => user.id === metaform.lastModifierId);

          return (
            <Box
              key={ metaform.id }
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e2e5e3",
                borderRadius: 1,
                px: { xs: 2, sm: 2.25 },
                py: 2
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={ 1.5 }
                alignItems={{ sm: "center" }}
              >
                <Box flex={ 1 } minWidth={ 0 }>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 15, fontWeight: 500, lineHeight: "22px"
                    }}
                  >
                    { metaform.title }
                  </Typography>
                  <Typography
                    sx={{
                      color: "#8a8f8c", fontSize: 12, lineHeight: "18px"
                    }}
                  >
                    { moment(metaform.modifiedAt).format("L LT") }
                    { lastModifier?.email ? ` · ${lastModifier.email}` : "" }
                  </Typography>
                </Box>
                <Chip
                  label={ strings.editorScreen.formProductionVersion }
                  sx={{
                    backgroundColor: "#e4efe9",
                    borderRadius: 0.75,
                    color: "#1c5f49",
                    fontSize: 12,
                    fontWeight: 500
                  }}
                />
                <Stack direction="row" spacing={ 0.75 } flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={ () => metaform.id && goToEditor(metaform.id) }
                    sx={{
                      borderColor: "rgba(39, 128, 98, 0.5)",
                      color: "#278062",
                      fontSize: 13,
                      fontWeight: 500
                    }}
                  >
                    { strings.generic.edit }
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={ () => {
                      setSelectedId(metaform.id);
                      setDeleteDialogOpen(true);
                    }}
                    sx={{
                      borderColor: "#e5c6c3", color: "#a33b33", fontSize: 13
                    }}
                  >
                    { strings.generic.delete }
                  </Button>
                </Stack>
              </Stack>
              { Boolean(versions.length) &&
                <>
                  <Divider sx={{ borderColor: "#f0f2f1", my: 1.5 }}/>
                  <Stack direction="row" spacing={ 0.75 } flexWrap="wrap" useFlexGap>
                    { versions.map(version => {
                      const colors = getVersionColors(version.type);
                      return (
                        <Chip
                          key={ version.id }
                          label={ getVersionLabel(version) }
                          onClick={ () => version.id && goToEditor(version.id) }
                          sx={{
                            backgroundColor: colors.backgroundColor,
                            borderRadius: 0.75,
                            color: colors.color,
                            cursor: "pointer",
                            fontSize: 12,
                            fontWeight: 500
                          }}
                        />
                      );
                    }) }
                  </Stack>
                </>
              }
            </Box>
          );
        }) }
      </Stack>
      <ConfirmDialog
        onClose={ closeDeleteDialog }
        onCancel={ closeDeleteDialog }
        onConfirm={ confirmDelete }
        cancelButtonText={ strings.generic.cancel }
        positiveButtonText={ strings.generic.confirm }
        title={ strings.editorScreen.confirmDeleteVersionTitle }
        text={ strings.editorScreen.confirmDeleteVersionText }
        open={ deleteDialogOpen }
      />
    </>
  );
};

export default EditorFormSelector;